import { forwardRef, useRef, useState, type CSSProperties, type HTMLAttributes, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon, type IconName } from '../../icons/Icon';
import { Keys } from '../../utils/keys';
import { Spinner } from '../Spinner/Spinner';
import { toneText } from '../Badge/tones';
import { useDsT } from '../../services/i18n';

export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  action?: ToastAction;
  onDismiss?: () => void;
  dismissible?: boolean;
  /** Pending state of a promise-bound toast: a spinner replaces the tone icon. */
  loading?: boolean;
  /** Drag the card sideways past a threshold to dismiss it (default true when dismissible). */
  swipeToDismiss?: boolean;
  /** 0 is the front of the stack; deeper cards are scaled down, offset and faded. */
  stackDepth?: number;
  /** Collapse the stack (false while the region is hovered / focused, which expands it). */
  stackCollapsed?: boolean;
  /** Which edge the stack grows from, so the cards behind peek out on the right side. */
  stackFrom?: 'top' | 'bottom';
}

const icons: Record<ToastTone, IconName> = {
  neutral: 'info',
  success: 'check-circle',
  warning: 'alert',
  danger: 'alert-circle',
  info: 'info',
};

const accent: Record<ToastTone, string> = {
  neutral: 'before:bg-border-strong',
  success: 'before:bg-success',
  warning: 'before:bg-warning',
  danger: 'before:bg-danger',
  info: 'before:bg-info',
};

/** How far the card must travel before releasing dismisses it. */
export const TOAST_SWIPE_THRESHOLD = 64;
const RUBBER = 0.35;
/** Visible cards in a collapsed stack; anything deeper is faded out entirely. */
export const TOAST_STACK_VISIBLE = 3;
const STACK_OFFSET = 8;

/** Resists past the dismiss threshold so the drag feels attached to the card. */
export function rubberBand(dx: number, threshold = TOAST_SWIPE_THRESHOLD): number {
  const distance = Math.abs(dx);
  if (distance <= threshold) return dx;
  return Math.sign(dx) * (threshold + (distance - threshold) * RUBBER);
}

/** Presentational toast card. Use `ToastProvider` + `useToast()` for stacking, timers and announcements. */
export const Toast = forwardRef<HTMLDivElement, ToastProps>(function Toast(
  {
    title,
    description,
    tone = 'neutral',
    action,
    onDismiss,
    dismissible = true,
    loading = false,
    swipeToDismiss = true,
    stackDepth = 0,
    stackCollapsed = false,
    stackFrom = 'bottom',
    className,
    style,
    onKeyDown,
    onPointerDown,
    ...rest
  },
  ref,
) {
  const [dx, setDx] = useState(0);
  const t = useDsT();
  const drag = useRef<{ startX: number; raw: number } | null>(null);
  const canSwipe = swipeToDismiss && dismissible && !!onDismiss;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (e.key === Keys.Escape && onDismiss) {
      e.stopPropagation();
      onDismiss();
    }
  };

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    onPointerDown?.(e);
    if (!canSwipe || e.button !== 0) return;
    // Never hijack a press on the action or close button.
    if (e.target instanceof HTMLElement && e.target.closest('button')) return;
    drag.current = { startX: e.clientX, raw: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current.raw = e.clientX - drag.current.startX;
    setDx(rubberBand(drag.current.raw));
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (!current) return;
    if (Math.abs(current.raw) > TOAST_SWIPE_THRESHOLD) onDismiss?.();
    setDx(0);
  };

  const depth = Math.max(0, stackDepth);
  const stacked = stackCollapsed && depth > 0;
  const hidden = stackCollapsed && depth >= TOAST_STACK_VISIBLE;
  const stackTransform = stacked
    ? `translateY(${stackFrom === 'bottom' ? -STACK_OFFSET * depth : STACK_OFFSET * depth}px) scale(${(1 - 0.04 * depth).toFixed(3)})`
    : '';
  const swipeTransform = dx !== 0 ? `translate3d(${Math.round(dx)}px, 0, 0)` : '';
  const transform = [swipeTransform, stackTransform].filter(Boolean).join(' ');
  const swipeFade = dx !== 0 ? Math.max(0.3, 1 - Math.abs(dx) / 240) : 1;
  const opacity = hidden ? 0 : swipeFade * (stacked ? Math.max(0, 1 - 0.25 * depth) : 1);

  const stackStyle: CSSProperties = {
    ...(transform ? { transform } : null),
    ...(opacity !== 1 ? { opacity } : null),
    transformOrigin: stackFrom === 'bottom' ? 'bottom center' : 'top center',
    zIndex: 100 - depth,
    ...(hidden ? { pointerEvents: 'none' as const } : null),
    ...style,
  };

  return (
    <div
      ref={ref}
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
      tabIndex={0}
      data-tone={tone}
      data-stack-depth={depth}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={stackStyle}
      className={cn(
        'zo-toast pointer-events-auto relative flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 overflow-hidden rounded-overlay bg-surface-3 p-3 type-body text-fg shadow-3 focus-ring',
        // The tone accent is a 2px edge that belongs to the surface, not a border around it.
        'before:absolute before:inset-y-0 before:start-0 before:w-0.5 before:content-[""]',
        accent[tone],
        canSwipe && 'touch-pan-y select-none',
        drag.current === null && 'transition-[transform,opacity] motion-base',
        className,
      )}
      {...rest}
    >
      <span className={cn('mt-px shrink-0', toneText[tone])}>
        {loading ? <Spinner size={16} aria-hidden="true" /> : <Icon name={icons[tone]} size={16} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="type-body-strong">{title}</div>
        {description != null ? <div className="mt-0.5 text-fg-muted">{description}</div> : null}
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-2 rounded-sm type-label text-accent-fg underline-offset-2 transition-colors motion-fast hover:underline focus-ring"
          >
            {action.label}
          </button>
        ) : null}
      </div>
      {dismissible && onDismiss ? (
        <button
          type="button"
          aria-label={t('ds.common.dismiss', 'Dismiss')}
          onClick={onDismiss}
          className="-me-1 -mt-1 inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-fg-muted transition-colors motion-fast hover:bg-hover hover:text-fg focus-ring"
        >
          <Icon name="x" size={14} />
        </button>
      ) : null}
    </div>
  );
});
